<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { dataManager } from '@/dataManager'
import VueFeather from 'vue-feather';

const emit = defineEmits<{
  (e: 'backButton', backUrl: string|null): void
  (e: 'userChanged'): void
}>()

const appEnabled = import.meta.env.VITE_APP_USAGE_ENABLED == "true"

const router = useRouter()

const dm = dataManager.getInstance()

const screenState = ref("initial")
const errorMessage = ref("")

const email = ref("")
const password = ref("")
const confirmPassword = ref("")

const ownUseEnabled = ref(false)
const minorUseEnabled = ref(false)

const ownBirthdate = ref('')
const minorsData = ref<{
  id: number
  name?: string
  birthdate?: string
}[]>([{id: 0}])

const conditionsAccepted = ref(false)

let conditionsShown = false

onMounted(() => {
  emit('backButton', null)
  if (dm.getCurrentUser()) {
    router.push('/')
  }
})

function process() {
  if ( ! (document.querySelector("form#login-form") as HTMLFormElement).reportValidity()) {
    return;
  }
  if (screenState.value == 'initial') {
    if (dm.existsUser(email.value)) {
      screenState.value = "login"
    } else {
      screenState.value = "signin"
    }
  } else if (screenState.value == 'login') {
    if (dm.loginUser(email.value, password.value)) {
      emit('userChanged')
      router.push('/')
    } else {
      errorMessage.value = 'Contraseña incorrecta'
    }
  } else if (screenState.value == 'signin') {
    if (validateAndRegisterUser()) {
      dm.loginUser(email.value, password.value);
      emit('userChanged')
      router.push('/')
    }
  } else {
    console.error("Wrong status: " + screenState.value);
  }
}

function validateAndRegisterUser(): boolean {
  let finalOwnBirthdate: Date|null = null
  if (ownUseEnabled.value) {
    if (! ownBirthdate.value) {
      errorMessage.value = "Tu fecha de nacimiento es obligatoria"
      return false
    }
    finalOwnBirthdate = new Date(ownBirthdate.value)
    if (dataManager.calculateAge(finalOwnBirthdate) < 16) {
      errorMessage.value = "Debes tener al menos 16 años para usar esta app"
      return false
    }
  }
  const finalMinorsData: {name: string, birthdate: Date}[] = []
  if (minorUseEnabled.value) {
    for (let i=0; i < minorsData.value.length; i++) {
      const minorData = minorsData.value[i]
      if (! minorData.name || ! minorData.name.trim()) {
        errorMessage.value = "El nombre de todos los menores es obligatorio"
        return false
      }
      if (! minorData.birthdate) {
        errorMessage.value = "La fecha de nacimiento de todos los menores es obligatoria"
        return false
      }
      const finalMinorBirthdate = new Date(minorData.birthdate)
      if (dataManager.calculateAge(finalMinorBirthdate) >= 16) {
        errorMessage.value = "Los menores deben tener menos de 16 años"
        return false
      }
      finalMinorsData.push({name: minorData.name.trim(), birthdate: finalMinorBirthdate})
    }
  }

  if (!ownUseEnabled.value && !minorUseEnabled.value) {
    errorMessage.value = "Debes escoger al menos un objetivo de uso"
    return false
  }

  if (!conditionsAccepted.value) {
    errorMessage.value = "Debes aceptar las condiciones de uso para usar esta app"
    return false
  }

  if (!dm.registerUser(email.value, password.value, finalOwnBirthdate, finalMinorsData)) {
    errorMessage.value = "Ha habido problemas al registrar tu usuario"
    return false
  }
  return true
}

function cancel() {
  screenState.value = 'initial'
  email.value = ''
  password.value = ''
  confirmPassword.value = ''
  ownBirthdate.value = ''
  minorsData.value = [{id: 0}]
  minorUseEnabled.value = false
  ownUseEnabled.value = false
  conditionsAccepted.value = false
}

function showTermsAndConditions() {
  (document.querySelector("dialog.termsAndConditions") as HTMLDialogElement).showModal()
  conditionsShown = true
}

function closeDialog() {
  (document.querySelector("dialog.termsAndConditions") as HTMLDialogElement).close()
  conditionsAccepted.value = false
}

function acceptConditions() {
  closeDialog();
  conditionsAccepted.value = true
}

watch(minorUseEnabled, () => {
  if (minorUseEnabled.value) {
    nextTick(() => {
      const addMinorButton = document.querySelector('#add-minor-data-button') as HTMLElement
      addMinorButton.scrollIntoView({ behavior: 'smooth'})
    })
  }
})

watch(ownUseEnabled, () => {
  if (ownUseEnabled.value) {
    nextTick(() => {
      const enableMinorButton = document.querySelector('.button-minoruse') as HTMLElement
      enableMinorButton.scrollIntoView({ behavior: 'smooth'})
    })
  }
})

watch(conditionsAccepted, (newValue, oldValue) => {
  if (newValue && !conditionsShown) {
    showTermsAndConditions()
  }
})

function addMinorData() {
  minorsData.value.push({
    id: minorsData.value.length
  })
  nextTick(() => {
    const addMinorButton = document.querySelector('#add-minor-data-button') as HTMLElement
    addMinorButton.scrollIntoView({ behavior: 'smooth'})
  })
}
</script>

<template>
  <div class="wrapper">
    <div class="logo-circle">
        <img alt="GoTDAH logo" class="logo" src="@/assets/logo-gotdah-horizontal.svg" />
    </div>
  </div>

  <h2 v-if="! appEnabled">La app <b>GoTDAH</b> estará disponible próximamente</h2>

  <form id="login-form"  @submit="console.log('Enter process'); process(); $event.preventDefault()" v-if="appEnabled">

    <label v-if="['initial', 'login', 'signin'].includes(screenState)"
      class="email-label">
      <p><span>Correo electrónico</span></p>
      <input v-model="email" type="email"
        id="email" name="email"
        placeholder="usuario@correo.es"
        form="login-form"
        required
        />
    </label>

    <label v-if="['login', 'signin'].includes(screenState)"
      class="password-label">
      <p><span>Contraseña</span></p>
      <input v-model="password" type="password"
        id="password" required form="login-form" placeholder="contraseña"
        :disabled="! ['login', 'signin'].includes(screenState)"/>
    </label>

    <label v-if="screenState == 'signin'"
      class="confirmpassword-label">
      <p><span>Confirmar contraseña</span></p>
      <input v-model="confirmPassword" type="password"
        id="confirmPassword" required form="login-form" placeholder="contraseña"
        :disabled="screenState != 'signin'"/>
    </label>

    <div class="appuse" v-if="screenState == 'signin'">
      <p>¿Para quién es este GoTDAH?</p>
      <p class="small-note">Puedes escoger varios</p>
    </div>

    <label class="checkbox-use button-ownuse" v-if="screenState == 'signin'">
      <input type="checkbox" id="ownuse" v-model="ownUseEnabled"/>Para mi</label>

    <label v-if="screenState == 'signin' && ownUseEnabled">
      <p><span>Tu fecha de nacimiento</span></p>
      <input v-model="ownBirthdate" type="date"
        id="birthdate" required form="login-form"
        :disabled="screenState != 'signin'" />
    </label>

    <label v-if="screenState == 'signin'" class="checkbox-use button-minoruse">
      <input type="checkbox" id="minoruse" v-model="minorUseEnabled"/>Para un menor (&lt;16 años)</label>

    <div v-if="screenState == 'signin' && minorUseEnabled">
      <div v-for="minorData in minorsData" :key="minorData.id" class="minor-data">
        <h3>Datos del menor {{ minorData.id + 1 }} <button type="button" @click="minorsData.splice(minorsData.indexOf(minorData), 1)">
          <VueFeather class="back-button-arrow" type="user-minus"/></button></h3>
        <label>
          <p><span>Nombre del menor</span></p>
          <input v-model="minorData.name"
            :disabled="screenState != 'signin'"
            maxlength="15"
            required placeholder="Nombre o mote del menor"
            />
        </label>
        <label>
          <p><span>Su fecha de nacimiento</span></p>
          <input v-model="minorData.birthdate" type="date"
            required :disabled="screenState != 'signin'" />
        </label>
      </div>
    </div>

    <button v-if="screenState == 'signin' && minorUseEnabled"
      type="button" @click="addMinorData()" id="add-minor-data-button">
      <VueFeather type="user-plus"/>Añadir otro menor</button>

    <label class="termsandconditions" v-if="screenState == 'signin'">
      <input type="checkbox" id="termsandconditions" v-model="conditionsAccepted"/>
      <span>He leído y acepto las
        <a href="#" @click="showTermsAndConditions()">condiciones de uso.</a>
      </span>
    </label>

    <p class="error">{{ errorMessage }}</p>

    <div class="buttons">
      <button @click="process()" form="login-form" type="submit">Entrar</button>
      <button @click="cancel()" v-if="screenState != 'initial'"
        class="secondary" type="button" form="login-form">Cancelar</button>
    </div>
  </form>

  <dialog class="termsAndConditions">
    <h3>Condiciones de uso y aviso de privacidad</h3>
    <p>Los datos que nos facilites a través del formulario, serán tratados por
      FUNDACIÓN INGADA, con la finalidad de darte de alta como usuario de la
      aplicación así como para la elaboración de estadísticas, con base legal en
      el consentimiento expreso. Tus datos no serán cedidos a tercero, salvo
      obligación legal.</p>
    <p>Los datos de salud que incorpores a los cuestionarios, serán tratados por
      FUNDACIÓN INGADA con la finalidad de generar informes personalizados sobre
      tu desempeño, evolución y bienestar en funciones de regulación emocional y
      salud física, así como para la elaboración de estadísticas. La legitimación
      para dicho tratamiento se basa en el consentimiento expreso. Los datos serán
      conservados mientras la cuenta esté activa y solo se usarán para los fines
      indicados.
      </p>
    <p>Puedes ejercer tus derechos de acceso, rectificación,
      supresión, oposición y limitación dirigiéndote a la dirección de correo
      electrónico: <a href="mailto:fundacioningada.dpo@convenceabogados.es">fundacioningada.dpo@convenceabogados.es</a></p>
    <p>Consulta la información adicional en nuestra política de privacidad:
      <a href="https://www.fundacioningada.net/privacidad.htm" target="_blank">https://www.fundacioningada.net/privacidad.htm</a></p>

    <div class="buttons">
      <button @click="acceptConditions()">Acepto</button>
      <button class="button secondary" @click="closeDialog()">No acepto</button>
    </div>
  </dialog>

</template>

<style scoped>
section .wrapper .logo {
  max-width: 100%;
  height: 100%;
  max-height: 400px;
  padding: 8%;
  width: 100%;
}

section .wrapper {
  flex-shrink: 1;
  flex-grow: 1;
  min-height: 200px;
  max-height: 50vh;

  display: flex;
  align-items: center;
  justify-content: center;
}

section .wrapper .logo-circle {
  display: flex;
  align-items: center;
  justify-content: center;

  max-height: 100%;
  max-width: 100%;
  aspect-ratio: 1;

  margin: 5%;
  background-color: var(--color-secondary-background);
  border-radius: 50%;
  box-shadow: 2px 2px 2px lightgray;
}

.hidden {
  display: none;
}

form {
  width: var(--content-max-width);
  max-width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

h3 {
  margin-bottom: 0;
  margin-top: 20px;

  button {
    display: inline-block;
    min-height: 0; min-width: 0;
    width: 25px;
    height: 25px;
    padding: 0;
    margin: 0;
    border: 2px solid var(--main-color);
    background-color: white;

    i {
      width: 15px;
      color: var(--main-color);
    }
  }
}

label {
  display: block;
  margin-top: 0;
  background-color: transparent;

  --input-field-width: 350px;
  width: var(--input-field-width);
  max-width: 100%;

  p {
    font-size: 10pt;
    margin-left: 10px;
    margin-bottom: -8px;
    position: relative;
    text-align: left;
  }
  span {
    background-color: white;
    padding-left: 3px;
    padding-right: 3px;
  }

  input {
    z-index: -1;
    font-size: 12pt;
    width: var(--input-field-width);
    max-width: 100%;
    height: 40px;
    border: 1px solid #ff6800;
    border-radius: 5px;

    padding: 10px;
    font-family: var(--input-font);
    &::placeholder {
      opacity: 0.5;
    }
  }
}

.status-signin .main-logo {
  height: 150px;
}

.error {
  font-size: 10pt;
  color: red;
  margin-top: 0;
}

label.termsandconditions {
  max-width: 350px;
  width: 100%;
  text-align: left;
  vertical-align: middle;
  margin-top: 10px;

  display: flex;
  align-items: center;

  input[type=checkbox] {
    width: 30px;
    height: 30px;
    margin-right: 10px;
    z-index: 1;
  }
}

.appuse {
  max-width: 350px;
  width: 100%;
  text-align: center;

  .small-note {
    font-size: smaller;
    font-style: italic;
    margin: 0;
  }
}

label.checkbox-use {
  display: inline-block;

  border: 2px solid #ff6800;
  border-radius: 20px;
  background-color: white;
  color: #ff6800;
  font-weight: bold;
  flex-grow: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px;

  margin-bottom: 5px;
  margin-top: 10px;
  cursor: pointer;

  font-family: var(--button-font);
  font-size: 10pt;

  &:has(input:checked) {
    background-color: #ff6800;
    color: white;
  }

  input {
    width: 0px;
    height: 0px;
  }

  &:has(input:focus) {
    border: 2px solid red;
  }
}

</style>
